import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';
import { getColor, isDark } from '@lucid/theme';

import Box from '../box/box';
import type { Lucid } from '../../types/types';
import { type LinkProps } from '../link/link';
import Container from '../container/container';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Heading from '../heading/heading';
import Text from '../text/text';
import Link from '../link/link';
import VisibleVideo from '../video/visible-video/visible-video';

export interface ContentBlockProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the variant for section headings */
  headingVariant?:
    | Lucid.Theme.Component.Typography.Hero.VariantKeys
    | Lucid.Theme.Component.Typography.Heading.VariantKeys;
  /** Passes the specific Link component props to the Link component */
  linkProps?: LinkProps;
  /** The section heading */
  heading?: string;
  /** The section text */
  text?: string;
  /** The text variant for the section */
  textVariant?: Lucid.Theme.Component.Typography.Paragraph.VariantKeys;
  /** The CTA link and text. */
  link?: Lucid.Directus.Generated.Links;
  /** An image to be placed next to the text. */
  media?: Lucid.Directus.Generated.Directus_Files;
  /** Adjusts position of the media element
   * @param 0 - the media position on mobile
   * @param 1 - the media position on desktop
   * @default['top', 'right']
   */
  mediaPosition?: ['top' | 'bottom' | 'hidden', 'left' | 'right'];
  /* The color of the text */
  textColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** The section background color */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Determines the percentage of the container that will be occupied by each section on desktop
   *  * @default
   * [45, 65]
   * @param text - [0] - the text/cta section (left)
   * @param media - [1] - the image/video section (right)
   */
  sectionWeights?: [text: number, media: number];
}

const ContentBlock: FC<PropsWithChildren<ContentBlockProps>> = ({
  className,
  headingVariant = 'heading2XL',
  textVariant = 'paragraphL400',
  heading,
  text,
  link,
  media,
  mediaPosition = ['top', 'right'],
  textColor = 'coolGrey.100',
  linkProps = {
    displayAsButton: false,
    variant: 'primaryAlternate',
    iconAnimation: 'navigational',
  },
  backgroundColor = 'transparent',
  sectionWeights = [50, 50],
}) => {
  const hasDarkBackground = isDark(backgroundColor as Lucid.Theme.Color.ValueKeys);
  const [textRatio, mediaRatio] = sectionWeights.map(num => (num / 100) * 100);

  return (
    <Box
      css={theme => ({
        background: getColor({
          color: backgroundColor,
          theme,
        }),
      })}
    >
      <Container
        css={theme => [
          {
            paddingTop: theme.space.s64,
            paddingBottom: theme.space.s64,
            display: 'flex',
            flexDirection: 'column-reverse',
            gap: theme.space.s64,
            [theme.breakpoints.bp500.min]: {
              paddingTop: theme.space.s80,
              paddingBottom: theme.space.s80,
            },
            [theme.breakpoints.bp900.min]: {
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.space.s40,
            },
          },
          mediaPosition[0] === 'bottom' && { flexDirection: 'column' },
          mediaPosition[1] === 'left' && { [theme.breakpoints.bp900.min]: { flexDirection: 'row-reverse' } },
        ]}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
      >
        <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: `1 1 ${textRatio}%` }}>
          {heading && (
            <Heading
              as="h2"
              css={theme => ({
                color: getColor({
                  color: textColor,
                  theme,
                }),
              })}
              variant={headingVariant}
            >
              {heading}
            </Heading>
          )}
          {text && (
            <Text
              css={theme => ({
                color: getColor({
                  color: textColor,
                  theme,
                }),
              })}
              variant={textVariant}
            >
              {text}
            </Text>
          )}
          {link && (
            <Link
              {...linkProps}
              css={{ width: 'fit-content' }}
              href={link.final_url}
              variant={
                linkProps.variant ??
                (hasDarkBackground ? (linkProps.displayAsButton ? 'secondary' : 'primaryInverse') : 'primary')
              }
            >
              {link.default_link_text}
            </Link>
          )}
        </Box>
        {media && (
          <Box
            css={theme => [
              {
                maxWidth: '660px',
                width: '100%',
                flex: `1 1 ${mediaRatio}%`,
              },
              mediaPosition[0] === 'hidden' && {
                display: 'none',
                [theme.breakpoints.bp1024.min]: {
                  display: 'block',
                },
              },
            ]}
          >
            {media.type === 'video/mp4' ? (
              <VisibleVideo shouldLoop description={media.translations?.[0]?.alt || ''} src={withImage(media)} />
            ) : (
              <ImageWithPlaceholder
                alt={media?.translations?.[0]?.alt || ''}
                aspectRatio="4:3"
                src={media.filename_disk ? withImage(media) : ''}
              />
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export { ContentBlock };
export default ContentBlock;
